import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import Select from 'react-select'
import { addMinutes, endOfDay, isToday, startOfDay, subMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import API from '../../../../api/api'
import { fetchInitialData, getBuildingOptions } from '../Steps/data/adminManager.data'
import {
  getBuildingOptionsPmOrFd,
  getCheckboxHandler,
  getFrequencyOptions,
  getIdFromObjectOrValue,
  getOption,
  getTimezone,
  handleTimeChange,
  transformFormStateToRequestData,
} from '../helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { selectUserData } from '../../../../reducers/userDataReducer'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Icon from '../../../Icons'
import Loader from '../../../Loader/Loader'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import BaseEvent from '../BaseEvent/BaseEvent'
import EditEvent from '../EditEvent/EditEvent'
import EditEventsSchedule from '../EditEventsSchedule/EditEventsSchedule'
import ImagesDatabase from '../ImagesDatabase/ImagesDatabase'
import MediaUploadInput from '../MediaUploadInput/MediaUploadInput'
import MediaUploadInputEditMode from '../MediaUploadInputEditMode/MediaUploadInputEditMode'
import PDFUpload from '../PDFUpload/PDFUpload'
import SetupAutomatedReminders from '../SetupAutomatedReminders/SetupAutomatedReminders'
import MobilePreview from '../../../EventManagement/common/components/mobilePreview/MobilePreview'

import { useGetReminderFormState } from './hooks'

const ReminderEvent = ({
  editMode,
  eventData,
  goBack,
  handleClose,
  repeatable,
  selectedBuildingId,
  userData,
}) => {
  const { t } = useTranslation(['create-event', 'common'])
  const { displaySnackbar } = useSnackbar()
  const [fetchState, setFetchState] = useState({})
  const [eventsToUpdate, setEventsToUpdate] = useState('false')
  const [isPopupOpen, togglePopup] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isPropertyManager = userData.user.role === 'PropertyManager'
  const isFrontDesk = userData.user.role === 'ShiftManager'
  const isPropertyManagerOrFrontDesk = ['PropertyManager', 'ShiftManager'].includes(
    userData.user.role,
  )
  const isManagerOrAdmin = ['Manager', 'Administrator'].includes(userData.user.role)

  useEffect(() => {
    if (isManagerOrAdmin) {
      ;(async () => {
        setFetchState(await fetchInitialData())
      })()
    }
  }, [isManagerOrAdmin])

  const buildingOptions = isPropertyManagerOrFrontDesk
    ? getBuildingOptionsPmOrFd(isPropertyManager, isFrontDesk, userData)
    : fetchState.buildings
      ? getBuildingOptions(fetchState.buildings)
      : []

  const [formState, setFormState] = useGetReminderFormState(
    eventData,
    editMode,
    selectedBuildingId,
    buildingOptions,
    t,
  )

  const handleFieldChange = ({ target: { name, value } }) => {
    if (name === 'date') {
      const newFrequencyOptions = getFrequencyOptions(value, t)

      setFormState((prev) => {
        if (prev.schedulerType && prev.schedulerType.value !== 'none') {
          const currentType = prev.schedulerType.value
          const updatedOption = newFrequencyOptions.find((opt) => opt.value === currentType)

          return {
            ...prev,
            [name]: value,
            schedulerType: updatedOption || prev.schedulerType,
          }
        }

        // Otherwise just update the date
        return { ...prev, [name]: value }
      })
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)

  const onSubmit = async () => {
    setIsSubmitting(true)
    const formData = await transformFormStateToRequestData(
      {
        ...formState,
        buildingData: buildingOptions.find(
          (b) => b.id === getIdFromObjectOrValue(formState.building),
        ),
      },
      'Reminder',
      [],
      isPropertyManagerOrFrontDesk,
      [],
    )
    API.createNewEvent(formData)
      .then(() =>
        displaySnackbar(
          t('create-event:reminderEvent.createdSuccessfully', { eventName: formState.eventName }),
        ),
      )
      .catch(() => {
        displaySnackbar(
          t('create-event:reminderEvent.errorCreating', { eventName: formState.eventName }),
          true,
        )
      })
      .finally(() => {
        setIsSubmitting(false)
        handleClose()
      })
  }

  const onSubmitEdit = () => {
    if (repeatable) {
      togglePopup(true)
    } else {
      setEventsToUpdate(false)
      editModeConfirmCallback()
    }
  }

  const editModeConfirmCallback = async () => {
    setIsSubmitting(true)
    const newData = await transformFormStateToRequestData(
      {
        ...formState,
        buildingData: buildingOptions.find(
          (b) => b.id === getIdFromObjectOrValue(formState.building),
        ),
      },
      'Reminder',
      [],
      isPropertyManagerOrFrontDesk,
      [],
    )
    togglePopup(false)
    API.updateEvent(eventData.id, newData, eventsToUpdate)
      .then(() => displaySnackbar(t('create-event:shared.changesSavedSuccessfully')))
      .catch(() => displaySnackbar(t('create-event:shared.errorSavingChanges'), true))
      .finally(() => {
        setIsSubmitting(false)
        handleClose()
      })
  }

  const editModeCancelCallback = () => {
    togglePopup(false)
  }

  const detailButtonEnabled =
    !!formState.building && !!formState.eventName && !!formState.description

  const timezone = useMemo(
    () => getTimezone(formState, buildingOptions),
    [buildingOptions, formState.building],
  )

  const { minStartTime, maxStartTime, minEndTime, maxEndTime } = useMemo(() => {
    const selectedDate = new Date(formState.date)

    const minTime = isToday(selectedDate)
      ? toZonedTime(new Date(), timezone)
      : startOfDay(toZonedTime(selectedDate, timezone))
    const maxTime = endOfDay(minTime)

    return {
      minStartTime: minTime,
      maxStartTime: subMinutes(maxTime, 15),
      minEndTime: addMinutes(minTime, 15),
      maxEndTime: maxTime,
    }
  }, [formState.date, timezone])

  useEffect(() => {
    if (editMode && eventData && timezone) {
      setFormState((prev) => ({
        ...prev,
        date: moment(eventData.date).tz(timezone).toDate(),
      }))
    }
  }, [editMode, timezone, eventData])

  const dateAndTimeButtonEnabled =
    !!formState.location && !!formState.date && !!formState.timeStart && !!formState.timeEnd

  const isLoading = !buildingOptions.length

  const config = {
    details: {
      header: t('create-event:baseEvent.steps.eventDetails'),
      canGoNext: detailButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <Select
              className="select-container"
              classNamePrefix="select-primary"
              options={buildingOptions}
              name="building"
              value={getOption(formState.building, buildingOptions)}
              onChange={(e) => setFormState((prev) => ({ ...prev, building: e }))}
              placeholder={t('create-event:shared.selectBuildingPlaceholder')}
            />
          ),
          label: t('create-event:shared.selectBuildingLabel'),
        },
        second: null,
        third: {
          render: () => (
            <TextField
              name="eventName"
              onChange={handleFieldChange}
              value={formState.eventName}
              placeholder={t('create-event:reminderEvent.enterReminderNamePlaceholder')}
              maxLength={100}
            />
          ),
          label: t('create-event:reminderEvent.reminderName'),
        },
        fourth: null,
        fifth: {
          render: () => (
            <TextAreaField
              name="description"
              onChange={handleFieldChange}
              value={formState.description}
              placeholder={t('create-event:reminderEvent.reminderDescriptionPlaceholder')}
              maxLength={1000}
            />
          ),
          label: t('create-event:reminderEvent.reminderDescription'),
        },
      },
    },
    mediaUpload: {
      header: t('create-event:progressTimeline.mediaUpload'),
      canGoNext: !!formState.image,
      editMode: editMode,
      inputs: {
        first: {
          label: t('create-event:shared.uploadEventPhotoLabel'),
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.image}
                handleFieldChange={handleFieldChange}
                buttonLabel={t('create-event:shared.replacePhotoButtonLabel')}
                name="image"
                accept="image/*"
                maxFileNameWidth="150px"
              />
            ) : formState.imagesDatabaseOpen ? (
              <ImagesDatabase formState={formState} handleFieldChange={handleFieldChange} />
            ) : (
              <MediaUploadInput
                eventName={formState.eventName}
                formState={formState}
                handleFieldChange={handleFieldChange}
              />
            ),
        },
        image:
          editMode && formState.image
            ? {
                image:
                  typeof formState.image === 'string'
                    ? getAssetLink({
                        path: 'chitown-images/training',
                        file: formState.image,
                        fallback: { file: 'training_image.png' },
                      })
                    : URL.createObjectURL(formState.file),
              }
            : null,
        second: {
          label: t('create-event:shared.uploadPdfOptionalLabel'),
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.pdf}
                handleFieldChange={handleFieldChange}
                buttonLabel={
                  formState.pdf
                    ? t('create-event:shared.replacePdfButtonLabel')
                    : t('create-event:shared.uploadPdfButtonLabel')
                }
                accept="application/pdf"
                name="pdf"
                maxFileNameWidth="250px"
              />
            ) : (
              <PDFUpload handleFieldChange={handleFieldChange} file={formState.pdf} />
            ),
        },
      },
    },
    dateAndTime: {
      header: t('create-event:baseEvent.steps.dateTime'),
      canGoNext: dateAndTimeButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <TextField
              leftIcon={<Icon icon="location2" />}
              name="location"
              placeholder={t('create-event:reminderEvent.enterReminderLocationPlaceholder')}
              onChange={handleFieldChange}
              value={formState.location}
            />
          ),
          label: t('create-event:shared.locationRequiredLabel'),
        },
        second: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              name="date"
              minDate={moment().toDate()}
              value={formState.date}
              onChange={handleFieldChange}
            />
          ),
          label: t('create-event:reminderEvent.reminderDateAndTime'),
        },
        third: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minStartTime}
              maxTime={maxStartTime}
              name="timeStart"
              value={formState.timeStart}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.startsLabel'),
        },
        fourth: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minEndTime}
              maxTime={maxEndTime}
              name="timeEnd"
              value={formState.timeEnd}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.endsLabel'),
        },
        fifth: editMode
          ? null
          : {
              render: () => (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  options={getFrequencyOptions(formState.date, t)}
                  name="schedulerType"
                  placeholder={t('create-event:shared.selectFrequencyPlaceholder')}
                  value={formState.schedulerType}
                  onChange={(e) => setFormState((prev) => ({ ...prev, schedulerType: e }))}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:shared.frequencyLabel'),
            },
        sixth: null,
        seventh: null,
      },
    },
    summary: {
      header: t('create-event:baseEvent.steps.attendees'),
      canGoNext: !isSubmitting,
      onSubmit: editMode ? onSubmitEdit : onSubmit,
      inputs: {
        first: {
          render: () => (
            <CheckboxField
              name="setupAutomatedReminders"
              onChange={handleCheckboxChange}
              value={formState.setupAutomatedReminders}
            />
          ),
          renderExtra: () =>
            formState.setupAutomatedReminders && (
              <SetupAutomatedReminders
                formState={formState}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
                eventType="reminder"
              />
            ),
          label: t('create-event:shared.setupAutomatedRemindersLabel'),
          tooltips: t('create-event:shared.setupAutomatedRemindersTooltip', {
            returnObjects: true,
          }),
        },
        second: null,
        third: null,
        fourth: null,
        fifth: null,
      },
    },
    footer: {
      cta: editMode
        ? t('create-event:shared.saveChangesCta')
        : t('create-event:reminderEvent.createAReminderCta'),
    },
  }
  return (
    <div className="events-modal">
      <div className="events-modal-card">
        {isLoading ? (
          <Loader style={{ width: '100px', height: '100px', left: '44%', top: '40%' }} />
        ) : editMode ? (
          <EditEvent config={config} eventType="reminder" handleClose={handleClose} />
        ) : (
          <BaseEvent
            config={config}
            goBack={goBack}
            eventType="reminder"
            handleClose={handleClose}
          />
        )}
        <ConfirmPopup
          isOpen={isPopupOpen}
          cancelCallback={editModeCancelCallback}
          confirmCallback={editModeConfirmCallback}
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:actions.confirm')}
          title={t('create-event:shared.cautionTitle')}
          body={
            <EditEventsSchedule
              onChange={(e) => setEventsToUpdate(e.target.value)}
              value={eventsToUpdate}
              formName="reminderEvent"
            />
          }
        />
      </div>
      <MobilePreview formState={formState} eventType="Reminder" />
    </div>
  )
}

ReminderEvent.propTypes = {
  editMode: PropTypes.bool,
  eventData: PropTypes.object,
  goBack: PropTypes.func,
  handleClose: PropTypes.func,
  repeatable: PropTypes.bool,
  selectedBuildingId: PropTypes.number,
  userData: PropTypes.object,
}

export default connect((state) => {
  const selectedBuilding = selectActiveBuilding(state)
  return {
    userData: selectUserData(state),
    selectedBuildingId: selectedBuilding ? selectedBuilding.id : null,
  }
})(ReminderEvent)
